import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { environment } from '../../environments/environment';

// Dev-only diagnostics for local MCD work (dashboard access and form validation).
// Enabled only for non-production builds served from localhost/private network
// addresses, so deployed staging/dev/production pages stay quiet. Production
// builds additionally replace this module with mcd-diagnostics.prod.ts (see
// angular.json), so no diagnostic code or log strings reach the deployed bundle.
const MCD_DIAG_PREFIX = '[MCD-DIAG]';
const lastLoggedState = new Map<string, string>();

export function areMcdDiagnosticsEnabled(): boolean {
  if (environment.production || typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host === '[::1]' ||
    host.endsWith('.local') ||
    /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host)
  );
}

export function logMcdEnvironmentDiagnostics(): void {
  if (!areMcdDiagnosticsEnabled()) {
    return;
  }
  console.log(`${MCD_DIAG_PREFIX} environment`, {
    production: environment.production,
    host: environment.host,
    client: environment.client,
    mcdDashboardAccessOverride: environment.mcdDashboardAccessOverride,
    origin: typeof window !== 'undefined' ? window.location.origin : null,
  });
}

export function logMcdDiagnostics(source: string, data: Record<string, unknown>): void {
  if (!areMcdDiagnosticsEnabled()) {
    return;
  }
  console.log(`${MCD_DIAG_PREFIX} ${source}`, data);
}

export function logMcdDiagnosticsOnce(
  source: string,
  stateKey: string,
  data: Record<string, unknown>
): void {
  if (!areMcdDiagnosticsEnabled()) {
    return;
  }
  if (lastLoggedState.get(source) === stateKey) {
    return;
  }
  lastLoggedState.set(source, stateKey);
  console.log(`${MCD_DIAG_PREFIX} ${source}`, data);
}

export interface McdFormControlDiagnostic {
  path: string;
  value: unknown;
  valid: boolean;
  errors: Record<string, unknown> | null;
  disabled: boolean;
}

export function collectMcdFormDiagnostics(form: AbstractControl): McdFormControlDiagnostic[] {
  const controls: McdFormControlDiagnostic[] = [];

  const visit = (control: AbstractControl, path: string): void => {
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach((name) => {
        visit(control.controls[name], path ? `${path}.${name}` : name);
      });
      return;
    }

    if (control instanceof FormArray) {
      control.controls.forEach((child, index) => visit(child, `${path}[${index}]`));
      return;
    }

    controls.push({
      path,
      value: control.value,
      valid: control.valid,
      errors: control.errors,
      disabled: control.disabled,
    });
  };

  visit(form, '');
  return controls;
}

export function logMcdFormDiagnosticsOnce(form: AbstractControl, reason: string): void {
  if (!areMcdDiagnosticsEnabled()) {
    return;
  }

  const controls = collectMcdFormDiagnostics(form);
  const invalid = controls.filter((control) => !control.valid);
  const report = {
    reason,
    valid: form.valid,
    invalid: invalid.map((control) => control.path),
    controls,
  };

  logMcdDiagnosticsOnce('MCD form diagnostics', JSON.stringify(report), report);
}
